import { h } from "preact";
import { useState, useRef, useEffect, useMemo } from "preact/hooks";

import { DEFAULT_DESCRIPTION } from "../../../apps/containers/AppContainer/Section/constants";
import BarrelActions from "../../../apps/data/barrel/BarrelActions";
import { formatTextArea } from "../../../apps/legacy/richText";

import {
    Variable,
    VariableWithPropertyName,
    VariableValue,
    VariableGroup,
    VariableCollection,
    VariableMode
} from "../../../foundation/api/model/variables/Variables";
import { PLATFORM } from "../../../foundation/enums";
import Color from "../../../foundation/model/color";

import { joinDOMElements } from "../../../foundation/utils/array";
import BasicRecord from "../../../foundation/utils/BasicRecord";
import classname from "../../../foundation/utils/classname";
import { stopPropagation } from "../../../foundation/utils/legacy";
import { copyContent } from "../../../foundation/utils/platform_ts";
import { pluralize } from "../../../foundation/utils/string";
import { isChildGroup } from "../../../foundation/utils/variables";

import DragDropContainer from "../../DragDropContainer/DragDropContainer";
import Alert from "../../v2/Alert";
import ColorBlock from "../../v2/ColorBlock";
import DropdownMenu, { DropdownMenuHorizontalAligment } from "../../v2/DropdownMenu";
import { DropdownButtonProps } from "../../v2/DropdownMenu/DropdownButton";
import IconArrowDown from "../../v2/Icons/IconArrowDown";
import IconMenu from "../../v2/Icons/IconMenu";
import { InputVariant } from "../../v2/Input";
import RichInput from "../../v2/Input/RichInput";
import Menu from "../../v2/Menu";
import OnlyIconButton, { OnlyIconButtonVariant } from "../../v2/OnlyIconButton";
import VariableContainer from "../../VariableContainer";
import DragDropVariableName from "../VariableName";

import styles from "./VariableGroupSection.module.css";

interface Props {
    platform: PLATFORM;
    collapsedSections: BasicRecord<boolean>;
    variableModeIdMap: BasicRecord<VariableMode>;
    sourceIdVariableMap: BasicRecord<Variable>;
    collection: VariableCollection;
    group: VariableGroup;
    canManageColors: boolean;
    onAttemptToChange: () => void | null;
    onCollapse: (ids: string[]) => void;
    onExpand: (ids: string[]) => void;
}

interface Context {
    styleguideContainerRef: () => HTMLElement;
}

function VariableGroupSection({
    platform,
    collapsedSections,
    variableModeIdMap,
    sourceIdVariableMap,
    collection,
    group,
    canManageColors,
    onAttemptToChange,
    onCollapse,
    onExpand
}: Props, { styleguideContainerRef }: Context) {
    const descriptionInputRef = useRef<HTMLInputElement>();

    const [deleteConfirmationDialogOpen, setDeleteConfirmationDialogOpen] = useState(false);
    const [editingDescription, setEditingDescription] = useState(false);
    const [dragMode, setDragMode] = useState(false);
    const [dragVariableId, setDragVariableId] = useState("");
    const [shouldCollapseButtonAnimate, setShouldCollapseButtonAnimate] = useState(false);

    const groupDescription = group.isDefault ? collection.description : group.description;
    const collapsed = collapsedSections[group._id];

    const variableCount = useMemo(() => {
        const groups = group.isDefault
            ? collection.groups
            : collection.groups.filter(item => isChildGroup(group.name, item.name));

        return groups.reduce((sum, childGroup) => sum + childGroup.variables.length, 0);
    }, [collection.groups, group.isDefault, group.name]);

    useEffect(() => {
        if (editingDescription) {
            if (descriptionInputRef && descriptionInputRef.current) {
                descriptionInputRef.current.focus();
            }
        }
    }, [editingDescription]);

    function handleExpandCollapseSection() {
        setShouldCollapseButtonAnimate(true);

        if (collapsed) {
            let groupsToExpand = [];

            if (group.isDefault) {
                groupsToExpand = collection.groups;

                mixpanel.master.track("Expanded variable collection");
            } else {
                groupsToExpand = collection.groups.filter(item => isChildGroup(group.name, item.name));

                mixpanel.master.track("Expanded variable group");
            }

            const groupIdsToExpand = groupsToExpand.map(item => item._id);

            onExpand(groupIdsToExpand);
        } else {
            let groupsToCollapse = [];

            if (group.isDefault) {
                groupsToCollapse = collection.groups;

                mixpanel.master.track("Collapsed variable collection");
            } else {
                groupsToCollapse = collection.groups.filter(item => isChildGroup(group.name, item.name));

                mixpanel.master.track("Collapsed variable group");
            }

            const groupIdsToCollapse = groupsToCollapse.map(item => item._id);

            onCollapse(groupIdsToCollapse);
        }
    }

    function handleEditDescriptionClick() {
        setEditingDescription(true);
    }

    function handleDeleteClick() {
        if (onAttemptToChange) {
            onAttemptToChange();
            return;
        }

        setDeleteConfirmationDialogOpen(true);
    }

    function handleDeleteConfirmationDialogClose() {
        setDeleteConfirmationDialogOpen(false);
    }

    function handleDelete() {
        BarrelActions.deleteVariableCollection({
            vcid: collection._id,
            syncWithApi: true
        });
    }

    function handleStartDrag(variableId: string) {
        setDragMode(true);
        setDragVariableId(variableId);
    }

    function handleEndDrag() {
        setDragMode(false);
        setDragVariableId("");
    }

    function handleDescriptionBlur(_: Event, { discard, value }: {
        discard: boolean;
        value: string;
    }) {
        if (editingDescription) {
            setEditingDescription(false);
        }

        if (discard || groupDescription === value) {
            return;
        }

        if (onAttemptToChange) {
            onAttemptToChange();
            return;
        }

        if (group.isDefault) {
            BarrelActions.updateVariableCollectionDescription({
                vcid: collection._id,
                description: value,
                oldDescription: groupDescription,
                syncWithApi: true
            });
        } else {
            BarrelActions.updateVariableCollectionGroupDescription({
                vcid: collection._id,
                gid: group._id,
                description: value,
                oldDescription: groupDescription,
                syncWithApi: true
            });
        }
    }

    function handleVariableReorder({ sourceId, sourceIndex, moveIndex }: {
        sourceId: string; sourceIndex: number; moveIndex: number;
    }) {
        if (onAttemptToChange) {
            onAttemptToChange();
            return;
        }

        BarrelActions.updateVariableOrder({
            vcid: collection._id,
            gid: group._id,
            variableId: sourceId,
            to: moveIndex,
            oldIndex: sourceIndex,
            syncWithApi: true
        });
    }

    function handleColorValueClick(color: Color) {
        const content = color.toFormattedString({ platform });

        copyContent({
            content,
            toastContainer: styleguideContainerRef()
        });
    }

    function handleCollapseButtonTransitionEnd() {
        setShouldCollapseButtonAnimate(false);
    }

    function renderOptionsMenuButton({ activated, onClick }: DropdownButtonProps) {
        return (
            <OnlyIconButton
                class={classname(styles.optionsButton, {
                    [styles.activated]: activated
                })}
                variant={OnlyIconButtonVariant.Ninja}
                icon={IconMenu}
                onClick={onClick}/>
        );
    }

    function renderOptionsMenu() {
        return (
            <DropdownMenu
                horizontalAlignment={DropdownMenuHorizontalAligment.Right}
                button={renderOptionsMenuButton}>
                <Menu.Button onClick={handleEditDescriptionClick}>
                    {groupDescription ? "Edit" : "Add"} description
                </Menu.Button>
                {group.isDefault && (
                    <Menu.Button warning onClick={handleDeleteClick}>
                        Delete collection
                    </Menu.Button>
                )}
            </DropdownMenu>
        );
    }

    function renderVariableValue(variableValue?: VariableValue) {
        if (!variableValue) {
            return null;
        }

        if (variableValue?.type === "color" || variableValue?.reference?.value?.type === "color") {
            const { color } = variableValue?.reference?.value || variableValue;
            const colorModel = new Color(color!);
            const colorOpacity = Math.round(color!.a * 100);

            return (
                <div class={styles.variableValue}>
                    <ColorBlock class={styles.colorPreview} color={colorModel.toRGBAString()}/>
                    {variableValue.reference ? (
                        <VariableContainer
                            variable={variableValue.reference.variable as VariableWithPropertyName}
                            variableModeIdMap={variableModeIdMap}
                            mappedVariables={sourceIdVariableMap}
                            direction="ltr"
                            expandOnHover={false}
                            toastContainer={styleguideContainerRef()}/>
                    ) : (
                        <span
                            class={classname(styles.variableColor, "body-1", "ellipsis")}
                            onClick={() => handleColorValueClick(colorModel)}>
                            {colorModel.toFormattedString({ platform })}
                            {colorOpacity < 100 &&
                                <span className={styles.colorAlpha}>{colorOpacity}%</span>
                            }
                        </span>
                    )}
                </div>
            );
        }

        return null;
    }

    function renderVariableNames() {
        return (
            <DragDropContainer onEndDrag={handleEndDrag}>
                {group.variables.map((variable, index) => (
                    <DragDropVariableName
                        key={`variableName-${variable._id}`}
                        index={index}
                        variable={variable}
                        dragMode={dragMode}
                        allowDropToTop={!index}
                        isDragging={variable._id === dragVariableId}
                        reorderEnabled={canManageColors}
                        onStartDrag={handleStartDrag}
                        onItemDrop={handleVariableReorder}/>
                ))}
            </DragDropContainer>
        );
    }

    function renderDeleteConfirmationDialog() {
        if (!deleteConfirmationDialogOpen) {
            return null;
        }

        return (
            <Alert
                title={`Delete ${collection.name}?`}
                description="Deleted collections cannot be restored. This will delete all variables and all groups in this collection."
                primaryButtonProps={{
                    children: "Delete Collection",
                    onClick: handleDelete
                }}
                secondaryButtonProps={{
                    children: "Cancel"
                }}
                onClose={handleDeleteConfirmationDialogClose}/>
        );
    }

    return (
        <div class={classname(styles.section, { [styles.collapsed]: collapsed })}>
            <div class={styles.header}>
                <div class={styles.headerTop}>
                    <div class={styles.metadata}>
                        <div class={classname(styles.nameContainer, "ellipsis")} onMouseDown={stopPropagation}>
                            <span class={classname(styles.name, "heading-3", "medium")}>{collection.name}</span>
                            {!group.isDefault && joinDOMElements(group.name.split("/").map(item => <span key={item} class={classname(styles.name, "heading-3", "medium")}>{item}</span>), " ")}
                        </div>
                        {collapsed && (
                            <span class={classname(styles.count, "body-1")}>
                                {`${pluralize(variableCount, "variable")} collapsed`}
                            </span>
                        )}
                    </div>
                    <div class={styles.buttons}>
                        <OnlyIconButton
                            class={classname(styles.collapseButton, {
                                [styles.animating]: shouldCollapseButtonAnimate
                            })}
                            icon={IconArrowDown}
                            variant={OnlyIconButtonVariant.Ninja}
                            title={collapsed ? "Expand section" : "Collapse section"}
                            onMouseDown={stopPropagation}
                            onClick={handleExpandCollapseSection}
                            onTransitionEnd={handleCollapseButtonTransitionEnd}/>
                        {canManageColors && renderOptionsMenu()}
                    </div>
                </div>
                {(groupDescription || editingDescription) && (
                    <RichInput
                        class={styles.descriptionInput}
                        variant={InputVariant.Ninja}
                        inputRef={descriptionInputRef}
                        value={groupDescription || DEFAULT_DESCRIPTION}
                        readOnly={!!onAttemptToChange || !canManageColors}
                        formatter={formatTextArea}
                        onClick={onAttemptToChange}
                        onBlur={handleDescriptionBlur}/>
                )}
            </div>
            {Boolean(group.variables.length) && !collapsed && (
                <div class={styles.table}>
                    <div onMouseDown={stopPropagation}>
                        <div class={classname(styles.modeName, "body-1 medium")}>Variable Names</div>
                        {renderVariableNames()}
                    </div>
                    <div class={styles.variableValues}>
                        {collection.modes.map(mode => (
                            <div class={styles.variableValueSet} key={mode._id} onMouseDown={stopPropagation}>
                                <div class={classname(styles.modeName, "body-1 medium ellipsis")}>{mode.name}</div>
                                {group.variables.map(variable => (
                                    renderVariableValue(variable.values.find(value => value.modeId === mode._id))
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {renderDeleteConfirmationDialog()}
        </div>
    );
}

export default VariableGroupSection;
